import { useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import { callBackbonePostApi } from '../../services/backboneApiService';
import useQuery from '../../utils/useQuery';

import MetaTags from '../../components/MetaTags';
import { Heading2 } from '../../components/Heading';
import { CustomButton } from '../../components/Buttons';

import DemoField from './DemoField';
import {
  DEFAULT_REP,
  DEMO_ACCENT,
  DEMO_SUCCESS,
  ERROR_MESSAGE,
  INTRO_TEXT,
  MAX_EMAIL_LENGTH,
  MAX_MOBILE_LENGTH,
  MAX_NAME_LENGTH,
  MAX_NOTES_LENGTH,
  MAX_ZIP_LENGTH,
  PAGE_TITLE,
  SMS_CONSENT_LABEL,
  SUBMIT_STATUS,
  SUCCESS_STATUS,
} from './constants';
import {
  buildIntakePayload,
  createEmptyForm,
  extractIntakeErrorMessage,
  readEventSettings,
  validateIntakeForm,
} from './helpers';

const nameRowSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 2,
  mt: 3,
};

const eventBarSx = {
  mt: 3,
  px: 2,
  py: 1.5,
  border: '1px solid rgba(255,255,255,0.14)',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 2,
};

const DemoIntake = () => {
  const query = useQuery();
  const submitLockRef = useRef(false);
  const firstNameRef = useRef(null);

  const settings = useMemo(() => readEventSettings(query), [query]);

  const [form, setForm] = useState(createEmptyForm);
  const [status, setStatus] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [checkedInName, setCheckedInName] = useState('');

  const isSaving = status === SUBMIT_STATUS.saving;
  const fieldsDisabled = isSaving;

  const clearStatus = () => {
    setStatus('');
    setSubmitError('');
  };

  const onChangeField = (key) => (event) => {
    const { value, checked, type } = event.target;
    setForm((prev) => ({ ...prev, [key]: type === 'checkbox' ? checked : value }));
    clearStatus();
  };

  /** The trailer tablet gets handed straight to the next walk-up. */
  const startNextIntake = () => {
    setForm(createEmptyForm());
    setStatus('');
    setSubmitError('');
    setCheckedInName('');
    firstNameRef.current?.focus();
  };

  const onSubmit = (event) => {
    event?.preventDefault?.();

    if (submitLockRef.current || isSaving) {
      return;
    }

    const validationError = validateIntakeForm(form);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    submitLockRef.current = true;
    setSubmitError('');
    setStatus(SUBMIT_STATUS.saving);

    const payload = buildIntakePayload({
      form,
      settings,
      pageUrl: window.location.href,
    });

    callBackbonePostApi(payload, '', { skipAuth: true })
      .then((response) => {
        if (response.status === SUCCESS_STATUS && response.data?.success) {
          setStatus(SUBMIT_STATUS.saved);
          setCheckedInName(form.firstName.trim());
        } else {
          const message =
            response?.data?.errorMessage || response?.data?.message || ERROR_MESSAGE.SUBMIT;
          setStatus('');
          setSubmitError(message);
          enqueueSnackbar(message, { variant: 'error' });
        }
      })
      .catch((error) => {
        const message = extractIntakeErrorMessage(error);
        setStatus('');
        setSubmitError(message);
        enqueueSnackbar(message, { variant: 'error' });
      })
      .finally(() => {
        submitLockRef.current = false;
      });
  };

  return (
    <Box>
      <MetaTags
        title={`Black Box VR - ${PAGE_TITLE}`}
        description="Check in before your Black Box VR demo."
      />

      <Box position="relative" display="flex" justifyContent="center" mb={6}>
        <Box
          component="form"
          noValidate
          maxWidth={612}
          width="100%"
          mt={8}
          px={2}
          onSubmit={onSubmit}
        >
          <Heading2>{PAGE_TITLE}</Heading2>

          <Typography mt={2} sx={{ opacity: 0.92, lineHeight: 1.6 }}>
            {INTRO_TEXT}
          </Typography>

          <Box sx={eventBarSx}>
            <Typography fontSize={13} sx={{ opacity: 0.75 }}>
              Event:{' '}
              <Box component="span" fontWeight={700} color={DEMO_ACCENT}>
                {settings.event}
              </Box>
            </Typography>
            {settings.rep !== DEFAULT_REP && (
              <Typography fontSize={13} sx={{ opacity: 0.75 }}>
                Rep:{' '}
                <Box component="span" fontWeight={700} color={DEMO_ACCENT}>
                  {settings.rep}
                </Box>
              </Typography>
            )}
          </Box>

          <Box sx={nameRowSx}>
            <DemoField
              required
              inputRef={firstNameRef}
              label="First name"
              autoComplete="given-name"
              placeholder="First name"
              maxLength={MAX_NAME_LENGTH}
              value={form.firstName}
              disabled={fieldsDisabled}
              onChange={onChangeField('firstName')}
            />
            <DemoField
              required
              label="Last name"
              autoComplete="family-name"
              placeholder="Last name"
              maxLength={MAX_NAME_LENGTH}
              value={form.lastName}
              disabled={fieldsDisabled}
              onChange={onChangeField('lastName')}
            />
          </Box>

          <DemoField
            required
            sx={{ mt: 3 }}
            type="email"
            label="Email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            maxLength={MAX_EMAIL_LENGTH}
            value={form.email}
            disabled={fieldsDisabled}
            onChange={onChangeField('email')}
          />

          <DemoField
            required
            sx={{ mt: 3 }}
            type="tel"
            label="Mobile number"
            autoComplete="tel"
            inputMode="tel"
            placeholder="(555) 555-5555"
            helperText="This is where your demo results are sent."
            maxLength={MAX_MOBILE_LENGTH}
            value={form.mobile}
            disabled={fieldsDisabled}
            onChange={onChangeField('mobile')}
          />

          <DemoField
            sx={{ mt: 3 }}
            label="Zip code (optional)"
            autoComplete="postal-code"
            inputMode="numeric"
            placeholder="83702"
            maxLength={MAX_ZIP_LENGTH}
            value={form.zipCode}
            disabled={fieldsDisabled}
            onChange={onChangeField('zipCode')}
          />

          <DemoField
            multiline
            sx={{ mt: 3 }}
            label="Anything we should know? (optional)"
            placeholder="Fitness goals, injuries, questions…"
            maxLength={MAX_NOTES_LENGTH}
            value={form.notes}
            disabled={fieldsDisabled}
            onChange={onChangeField('notes')}
          />

          <FormControlLabel
            sx={{ mt: 3, alignItems: 'flex-start' }}
            disabled={fieldsDisabled}
            control={
              <Checkbox
                color="warning"
                checked={form.receiveSms}
                onChange={onChangeField('receiveSms')}
                sx={{ pt: 0 }}
              />
            }
            label={
              <Typography fontSize={14} sx={{ opacity: 0.85, lineHeight: 1.5 }}>
                {SMS_CONSENT_LABEL}
              </Typography>
            }
          />

          {submitError && (
            <Typography mt={3} color="error" role="alert" id="intake-submit-error">
              {submitError}
            </Typography>
          )}

          <CustomButton
            type="submit"
            sx={{ mt: 4, mb: 2 }}
            disabled={isSaving}
            loading={isSaving}
            aria-describedby={submitError ? 'intake-submit-error' : undefined}
          >
            {isSaving ? 'Checking in…' : "I'm Checked In"}
          </CustomButton>
        </Box>
      </Box>

      <Dialog
        open={status === SUBMIT_STATUS.saved}
        onClose={startNextIntake}
        aria-labelledby="intake-success-title"
        aria-describedby="intake-success-description"
        PaperProps={{
          sx: {
            backgroundColor: '#1b1b1b',
            color: '#fff',
            backgroundImage: 'none',
            minWidth: { xs: '90%', sm: 420 },
          },
        }}
      >
        <DialogContent sx={{ pt: 4 }}>
          <Typography
            id="intake-success-title"
            component="h2"
            fontWeight={800}
            fontSize={24}
            color={DEMO_SUCCESS}
          >
            {checkedInName ? `You're set, ${checkedInName}!` : "You're set!"}
          </Typography>
          <DialogContentText
            id="intake-success-description"
            sx={{ color: 'rgba(255,255,255,0.85)', mt: 1.5 }}
          >
            Hand the tablet back to your rep — your demo results will come through after you play.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <CustomButton onClick={startNextIntake} autoFocus>
            Next person
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DemoIntake;
